import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useCallback, useMemo } from 'react';

/** Item kinds that can be archived. Keep in sync with the backend store. */
export type ArchiveItemType = 'notification' | 'run' | 'approval';

/**
 * Client for the per-user archive store (`/api/archive`). Archiving hides an
 * item from the current user's own view of notifications / runs / approvals; it
 * never affects teammates and never deletes the underlying item.
 */
export function useArchiveApi() {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const base = useCallback(
    () => discoveryApi.getBaseUrl('archive'),
    [discoveryApi],
  );

  const list = useCallback(
    async (type: ArchiveItemType): Promise<Set<string>> => {
      const baseUrl = await base();
      const res = await fetchApi.fetch(`${baseUrl}/items?type=${type}`);
      if (!res.ok) throw new Error(await res.text());
      const { items } = (await res.json()) as {
        items: Array<{ itemId: string; archivedAt: string }>;
      };
      return new Set(items.map(i => i.itemId));
    },
    [base, fetchApi],
  );

  const archive = useCallback(
    async (type: ArchiveItemType, itemId: string) => {
      const baseUrl = await base();
      const res = await fetchApi.fetch(`${baseUrl}/items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, itemId }),
      });
      if (!res.ok) throw new Error(await res.text());
    },
    [base, fetchApi],
  );

  const unarchive = useCallback(
    async (type: ArchiveItemType, itemId: string) => {
      const baseUrl = await base();
      const res = await fetchApi.fetch(
        `${baseUrl}/items/${type}/${encodeURIComponent(itemId)}`,
        { method: 'DELETE' },
      );
      if (!res.ok) throw new Error(await res.text());
    },
    [base, fetchApi],
  );

  // Stable object identity: list/archive/unarchive are useCallback-memoized, so
  // memoizing the wrapper keeps `useArchiveApi()` referentially stable across
  // renders. Consumers put it in effect deps; an unstable object would re-fire
  // their effects every render (infinite fetch loop).
  return useMemo(
    () => ({ list, archive, unarchive }),
    [list, archive, unarchive],
  );
}
